import dayjs from "dayjs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styles from "./styles.module.scss";
import { Fragment, useMemo } from "react";
import { uniqueId } from "lodash";
import { useAppState } from "@/modules/app/useAppContext";
import { TooltipProps } from "recharts";
import { Payload as LegendPayload } from "recharts/types/component/DefaultLegendContent";
import { NativeSelect, Stack } from "@altimateai/lego";

interface AxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value: string | number;
  };
}

const CustomYAxisTick = ({ x, y, payload }: AxisTickProps) => {
  const { currencySymbol } = useAppState();
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="end" dy={5} fill="#66768D">
        {currencySymbol}
        {payload?.value}
      </text>
    </g>
  );
};

const CustomXAxisTick = ({ x, y, payload }: AxisTickProps) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dx={-24} dy={4} transform="rotate(270)" textAnchor="middle" fill="#66768D">
        {payload?.value ? dayjs(payload.value).format("MMM-DD") : ""}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.chart_tooltip}>
        <div className={styles.tooltip_header}>Date</div>
        <div className={styles.tooltip_execs}>{dayjs(label).format("DD-MMM-YYYY")}</div>
        {payload.map(item => (
          <Fragment key={item.name}>
            <div className={styles.tooltip_header}>{item.name}</div>
            <div className={styles.tooltip_execs}>
              {typeof item.value === "number" ? item.value.toFixed(2) : item.value}
            </div>
          </Fragment>
        ))}
      </div>
    );
  }

  return null;
};

interface CustomLegendContainerProps {
  payload?: LegendPayload[];
}

const CustomLegendContainer = ({ payload }: CustomLegendContainerProps) => {
  return (
    <div className={styles.custom_legend}>
      {payload?.map((entry, index) => (
        <Stack key={`item-${index}`} align="center" className="gap-xs">
          <div style={{ width: "1em", height: "1em", background: entry.color }} />
          <div>{entry.value}</div>
        </Stack>
      ))}
    </div>
  );
};

interface BarShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  className?: string;
}

function isBarShapeProps(value: unknown): value is BarShapeProps {
  return typeof value === "object" && value !== null;
}

interface BarGraphProps {
  graphData: Record<string, string | number>[];
  colorsMap: Record<string, string>;
  yLabel: string;
  dataKeys: string[];
}

const BarGraph = ({ graphData, colorsMap, yLabel, dataKeys }: BarGraphProps) => {
  return (
    <ResponsiveContainer minHeight={300}>
      <BarChart data={graphData}>
        <CartesianGrid vertical={false} />
        <Legend
          verticalAlign="bottom"
          wrapperStyle={{ bottom: -24 }}
          content={<CustomLegendContainer />}
        />
        <XAxis
          dataKey="date"
          angle={-90}
          label={{
            value: "Date",
            position: "insideBottomRight",
            fill: "black",
          }}
          tick={<CustomXAxisTick />}
          interval={0}
          padding={{ right: 36 }}
        />
        <YAxis
          label={{
            value: yLabel,
            position: "insideTopLeft",
            fill: "black",
            offset: 0,
          }}
          padding={{ top: 20 }}
          tick={<CustomYAxisTick />}
        />
        <Tooltip content={<CustomTooltip />} />
        {dataKeys.map((item, i) => (
          <Bar
            key={item}
            barSize={8}
            dataKey={item}
            fill={colorsMap[item]}
            stackId="single"
            shape={(props: unknown) => {
              const radius: [number, number, number, number] =
                i === dataKeys.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0];
              if (!isBarShapeProps(props)) {
                return <Rectangle radius={radius} />;
              }
              return <Rectangle {...props} radius={radius} />;
            }}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

interface TimeFrameOption {
  label: string;
  value: string | number;
}

interface TimeFrameSelectorDropdownProps {
  options: TimeFrameOption[];
  period: number;
  setPeriod: (value: string | number) => void;
}

const TimeFrameSelectorDropdown = ({
  options,
  period,
  setPeriod,
}: TimeFrameSelectorDropdownProps) => {
  const getLabelByPeriod = (p: string | number) => {
    return options.find(opt => opt.value === p)?.label ?? "";
  };
  const id = useMemo(() => uniqueId("graph-period-dropdown-"), []);

  return (
    <NativeSelect
      id={id}
      onChange={val => setPeriod(Number(val))}
      value={String(period)}
      options={options.map(p => ({
        label: p.label,
        value: String(p.value),
      }))}
      size={"sm"}
      label={getLabelByPeriod(period)}
    />
  );
};

export { BarGraph, TimeFrameSelectorDropdown };
